import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Filter, 
  Target, 
  Users, 
  Search, 
  BarChart3, 
  MessageSquare, 
  Sparkles,
  ArrowRight,
  Zap,
  Eye,
  TrendingUp
} from 'lucide-react';

const aiAssistants = [
  {
    id: 'goal-based',
    title: 'Goal-Based AI Assistant',
    description: 'Get personalized social media strategy recommendations based on your specific goals and challenges.',
    features: ['Strategic Planning', 'Goal Setting', 'Performance Analysis', 'Actionable Insights'],
    icon: Target,
    link: '/dashboard/ai-goal-assistant',
    category: 'Strategy',
    status: 'Active'
  },
  {
    id: 'content-filtering',
    title: 'Enhanced Content Analysis',
    description: 'Multi-model AI content analysis with sentiment detection, emotion analysis, and toxicity scoring.',
    features: ['Sentiment Analysis', 'Emotion Detection', 'Toxicity Scoring', 'Intent Analysis'],
    icon: Filter,
    link: '/ai-lab',
    category: 'Content',
    status: 'Active'
  },
  {
    id: 'profile-agent',
    title: 'AI Profile Agent',
    description: 'Analyzes your profile and generates personalized content filtering strategies using advanced MCP protocols.',
    features: ['Profile Analysis', 'Smart Filtering', 'Behavior Learning', 'Personalization'],
    icon: Users,
    link: '/ai-profile-agent',
    category: 'Personalization',
    status: 'Active'
  },
  {
    id: 'social-listening',
    title: 'Social Listening AI',
    description: 'AI-enhanced monitoring across Reddit, Hacker News, and 17+ major news outlets with sentiment analysis.',
    features: ['Real-time Monitoring', 'Sentiment Analysis', 'Trend Detection', 'News Scanning'],
    icon: Search,
    link: '/dashboard/social-listening',
    category: 'Monitoring',
    status: 'Active'
  },
  {
    id: 'smart-recommendations',
    title: 'Smart Recommendations',
    description: 'Personalized content suggestions powered by machine learning and behavioral analysis.',
    features: ['Content Curation', 'Behavioral Learning', 'Performance Tracking', 'Click-through Analysis'],
    icon: Sparkles,
    link: '/ai-lab',
    category: 'Discovery',
    status: 'Active'
  },
  {
    id: 'content-moderation',
    title: 'Content Moderation AI',
    description: 'Advanced content filtering with nuanced context-aware decisions and multi-factor analysis.',
    features: ['Context-Aware Filtering', 'Multi-Factor Analysis', 'Rule-Based Logic', 'Intent Detection'],
    icon: Eye,
    link: '/dashboard/content-filtering',
    category: 'Safety',
    status: 'Active'
  },
  {
    id: 'facebook-assistant',
    title: 'Facebook AI Assistant',
    description: 'Platform-specific AI optimization for Facebook feeds with engagement analysis.',
    features: ['Feed Optimization', 'Engagement Analysis', 'Post Scheduling', 'Audience Insights'],
    icon: MessageSquare,
    link: '/dashboard/platforms',
    category: 'Platform',
    status: 'Active'
  },
  {
    id: 'analytics-ai',
    title: 'AI Analytics Engine',
    description: 'Intelligent analytics and reporting with predictive insights and performance optimization.',
    features: ['Predictive Analytics', 'Performance Insights', 'Trend Forecasting', 'ROI Analysis'],
    icon: BarChart3,
    link: '/dashboard/analytics',
    category: 'Analytics',
    status: 'Active'
  }
];

const categories = ['All', 'Strategy', 'Content', 'Personalization', 'Monitoring', 'Discovery', 'Safety', 'Platform', 'Analytics'];

export default function AIAssistants() {
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredAssistants = selectedCategory === 'All' 
    ? aiAssistants 
    : aiAssistants.filter(assistant => assistant.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              AI Assistants Hub
            </h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover and access all AI-powered features designed to enhance your social media management experience.
            Each assistant specializes in different aspects of content analysis, strategy, and optimization.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="transition-all duration-200"
            >
              {category}
            </Button>
          ))}
        </div>

        {/* AI Assistants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {filteredAssistants.map((assistant) => {
            const IconComponent = assistant.icon;
            return (
              <Card key={assistant.id} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20 bg-card/50 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {assistant.category}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                        {assistant.status}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-lg leading-tight">{assistant.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {assistant.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-1">
                      {assistant.features.map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <Button asChild className="w-full group/btn">
                      <Link to={assistant.link} className="flex items-center justify-center gap-2">
                        Launch Assistant
                        <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-6 text-center">
              <Zap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Intelligent Automation</h3>
              <p className="text-sm text-muted-foreground">
                AI-powered automation reduces manual work and increases efficiency across all platforms.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-6 text-center">
              <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Real-time Insights</h3>
              <p className="text-sm text-muted-foreground">
                Get instant analytics and recommendations to optimize your social media strategy.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-6 text-center">
              <Brain className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Advanced AI Models</h3>
              <p className="text-sm text-muted-foreground">
                Leveraging cutting-edge AI technology for superior content analysis and decision making.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}